import React, { useState, useEffect, forwardRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import urls from "../../../constants";
import { CurrencyConvertion, FormatValueDecimal } from "../../../utils/Validate";

// These values have dedicated columns in the quantity-entry grid.
const HIDDEN_VARIANT_IDS = [8, 12, 13, 14, 15];

const ExampleCustomInput = forwardRef(({ value, onClick, disabled }, ref) => (
    <input
        value={value}
        className="requested-delivery-date-input"
        onClick={onClick}
        ref={ref}
        placeholder="DD/MM/YYYY"
        readOnly
        disabled={disabled}
    />
));

const TableRow = ({
    currencyDetail,
    productitem,
    productindex,
    variantsArray,
    productConfigVariant,
    commentOpen,
    setCommentOpen,
    onProductChange,
    onItemDeliveryDateChange,
    onNotesChange,
    values,
    factoryOrderRecord,
    releaseForm,
    isFactoryOrderEdit,
    isFactoryOrderReleased,
    isFactoryOrderLocked
}) => {
    const list = variantsArray?.filter(d => d?.sid === productitem?.sid) ?? [];
    const isInCurrentOrder = productitem?.isInCurrentOrder !== false;
    const factoryOrderProduct = factoryOrderRecord?.products?.find(
        p => String(p?.customerOrderProductId) === String(productitem?.orderProductId ?? productitem?.quoteProductId ?? productitem?.id)
    );
    const factoryOrderItem = factoryOrderProduct?.productItems?.find(item =>
        String(
            item?.customerOrderProductItemId ??
            item?.quoteProductItemId ??
            item?.orderProductItemId ??
            item?.id
        ) === String(productitem?.id ?? productitem?.quoteProductItemId ?? productitem?.orderProductItemId)
    );
    const factoryReleaseItem = factoryOrderItem || (factoryOrderRecord?.items || []).find(item =>
        String(
            item?.customerOrderProductItemId ??
            item?.quoteProductItemId ??
            item?.orderProductItemId ??
            item?.customerOrderItemId ??
            item?.id
        ) === String(
            productitem?.customerOrderProductItemId ??
            productitem?.quoteProductItemId ??
            productitem?.orderProductItemId ??
            productitem?.customerOrderItemId ??
            productitem?.id
        )
    );

    const visibleColumns = (productConfigVariant || [])
        .filter(v => !urls?.HIDDEN_FIELDS_GROUP?.includes(v?.groupId))
        .filter(v => !HIDDEN_VARIANT_IDS.includes(v?.variantId))
        .sort((a, b) => (a?.sequence ?? 0) - (b?.sequence ?? 0));

    const isNegativeMargin = Number(productitem?.margin || 0) < 0;

    const quoteQty = Number(productitem?.qtyValues ?? productitem?.quoteQuantity ?? 0);

    const customerOrderQty = Number(productitem?.lpoQuantity ?? 0);

    const releasedQty = Number(productitem?.releasedQuantity ?? 0);
    const releasedLimitQty = Number(
        factoryOrderItem?.currentReleaseQuantity ??
        factoryOrderItem?.factoryReleasedQuantity ??
        productitem?.releasedQuantity ??
        productitem?.factoryReleasedQuantity ??
        0
    );
const currentReleasedQty = Number(
    productitem?.currentReleasedQuantity ??
    Math.max(Number(customerOrderQty) - Number(releasedQty), 0)
);

    const getDateValue = (value, fallback) => {
        const dateValue = value || fallback;
        if (!dateValue) return null;
        // Requested delivery dates are calendar dates. Do not let an API UTC
        // timestamp (for example 2026-10-14T00:00:00Z) move it to the
        // previous day in the browser's timezone.
        if (typeof dateValue === "string") {
            const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateValue);
            if (match) {
                return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
            }
        }
        const date = new Date(dateValue);
        return Number.isNaN(date.getTime()) ? null : date;
    };

    const [
        editableCurrentReleasedQty,
        setEditableCurrentReleasedQty
    ] = useState(currentReleasedQty);

    useEffect(() => {
        setEditableCurrentReleasedQty(currentReleasedQty);
    }, [
        productitem?.sid,
        productitem?.lpoQuantity,
        productitem?.isInCurrentOrder,
        productitem?.currentReleasedQuantity,
        productitem?.factoryReleasedQuantity,
        productitem?.releasedQuantity,
        currentReleasedQty
    ]);

    const [rowErrorMsg, setRowErrorMsg] = useState("");

    const handleClearError = () => {
        if (rowErrorMsg) setRowErrorMsg("");
    };

const handleCurrentReleasedChange = (e) => {
    const enteredValue = e.target.value;

    if (!isInCurrentOrder) {
        setEditableCurrentReleasedQty(0);
        setRowErrorMsg("");
        return;
    }

    if (enteredValue === "") {
        setEditableCurrentReleasedQty("");
        setRowErrorMsg("");
        onProductChange(e, productitem);
        return;
    }

    const numericValue = Number(enteredValue);

    if (Number.isNaN(numericValue)) {
        return;
    }

    const lpoQty = Number(productitem?.lpoQuantity ?? 0);
    const availableQty = isFactoryOrderReleased
        ? Math.max(releasedLimitQty, 0)
        : Math.max(lpoQty, 0);

    if (numericValue > availableQty) {
        setRowErrorMsg(
            isFactoryOrderReleased
                ? `Released factory order quantity cannot be greater than ${availableQty}.`
                : `Factory order quantity has only ${availableQty} quantity available.`
        );
        return;
    }

    setEditableCurrentReleasedQty(enteredValue);
    setRowErrorMsg("");

    onProductChange(e, productitem);
};
    return (
        <tr className={isNegativeMargin ? "negative-margin-row" : ""}>
            {visibleColumns.map((colVariant, varindex) => {
                const match = list.find(d => d?.variantId === colVariant?.variantId);
                return varindex === 0 ? (
                    <td className="text-end order-size-serial-cell" key={colVariant?.variantId ?? varindex}>
                        <div className="custom-checkbox custom-checkbox-blue d-flex justify-content-center">
                            {productindex + 1}
                        </div>
                    </td>
                ) : (
                    <td
                        className="text-end"
                        data-label={colVariant?.variantName}
                        key={colVariant?.variantId ?? varindex}
                    >
                        {match?.variantValue || ""}
                    </td>
                );
            })}

            <td className="text-end" data-label="List price">
                {FormatValueDecimal(
                    CurrencyConvertion(
                        Number(productitem?.listPriceValues) || 0,
                        Number(values?.conversionRate) || 1,
                        currencyDetail?.shortCode
                    )
                )}
            </td>
            <td className="text-end" data-label="CO qty">{productitem?.lpoQuantity || "0"}</td>
            <td className="text-end" data-label="Released qty">
                {factoryOrderItem?.factoryReleasedQuantity ?? productitem?.releasedQuantity ?? 0}
            </td>
            <td className="lpo-qty-cell" data-label="Current release">
                <input
                    className={`form-control${rowErrorMsg ? " input-error" : ""}`}
                    name="currentReleasedQuantity"
                    type="number"
                    min="0"
                    max={isInCurrentOrder ? (isFactoryOrderReleased ? releasedLimitQty : Number(productitem?.lpoQuantity ?? 0)) || undefined : 0}
                    value={editableCurrentReleasedQty}
                    disabled={!isInCurrentOrder || isFactoryOrderLocked}
                    onFocus={handleClearError}
                    onClick={handleClearError}
                    onChange={handleCurrentReleasedChange}
                />
                {rowErrorMsg && (
                    <span className="field-error-tooltip">{rowErrorMsg}</span>
                )}
            </td>
            <td className="text-end" data-label="Margin">{Number(productitem?.margin || 0)}</td>
            <td className="text-end" data-label="Net total">
                {FormatValueDecimal(Number(productitem?.totalPrice) || 0)}
            </td>
            <td data-label="Notes">
                <div className="notes-cell" title={productitem?.notes}>
                    {productitem?.notes}
                </div>
            </td>
            <td className="req-dely-date" data-label="Req delivery date">
                <DatePicker
                    selected={getDateValue(
                        (productitem?.__requestedDeliveryDateChanged
                            ? productitem?.requestedDeliveryDate
                            : undefined) ??
                        factoryReleaseItem?.requestedDeliveryDate ??
                        factoryReleaseItem?.effectiveRequestedDeliveryDate ??
                        productitem?.requestedDeliveryDate ??
                        productitem?.effectiveRequestedDeliveryDate,
                        releaseForm?.deliveryFromDate ?? releaseForm?.scheduledDeliveryDate ?? values?.scheduledDeliveryDate
                    )}
                    onChange={(date) => onItemDeliveryDateChange && onItemDeliveryDateChange(date, productitem)}
                    customInput={<ExampleCustomInput disabled={!isInCurrentOrder || isFactoryOrderLocked} />}
                    dateFormat="dd/MM/yyyy"
                    disabled={!isInCurrentOrder || isFactoryOrderLocked}
                    popperPlacement="bottom-end"
                    minDate={new Date()}
                    popperProps={{ strategy: "fixed" }}
                />
            </td>
        </tr>
    );
};

export default TableRow
